<script setup lang="ts">
// Pick a conversation to share a library item into.
import { computed, ref } from 'vue'
import type { LibraryRef, PartyMedia } from '../api/types'
import { useParty } from '../stores/party'
import { usePlayer } from '../stores/player'
import { describeError } from '../api/http'
import { dmTitle } from '../lib/format'
import { useSession } from '../stores/session'
import Modal from './Modal.vue'

const props = defineProps<{ item: LibraryRef; mode?: 'share' | 'party'; queue?: PartyMedia[] }>()
const emit = defineEmits<{ close: [] }>()
const session = useSession()
const busy = ref(false)
const note = ref('')

const targets = computed(() => [
  ...session.spaces.flatMap((s) => s.channels.map((c) => ({ id: c.id, label: `${s.name} › #${c.name}` }))),
  ...session.dms.map((c) => ({
    id: c.id,
    label: `@ ${dmTitle(session.profiles, c.members, session.me, c.name)}`,
  })),
])

const party = useParty()
const player = usePlayer()

async function share(conv: string): Promise<void> {
  busy.value = true
  try {
    if (props.mode === 'party') {
      player.stop()
      await party.start(conv, props.queue ?? [{ source: 'library', item: props.item }])
      session.toast('Party started — friends can join from the chat')
    } else {
      await session.shareLibraryItem(conv, props.item, note.value.trim() || undefined)
      session.toast(`Shared ${props.item.title}`)
    }
    emit('close')
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <Modal
    :title="mode === 'party' ? `Watch “${item.title}” together in…` : `Share “${item.title}”`"
    @close="emit('close')"
  >
    <input v-if="mode !== 'party'" v-model="note" class="input mb-3" placeholder="Add a note (optional)" maxlength="500" />
    <div class="max-h-72 overflow-y-auto border-t border-border">
      <button
        v-for="t in targets"
        :key="t.id"
        class="flex w-full items-center justify-between border-b border-border px-1 py-2.5 text-left text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary disabled:opacity-40"
        :disabled="busy"
        :data-testid="`share-target-${t.label}`"
        @click="share(t.id)"
      >
        {{ t.label }}<span class="mono text-text-disabled">→</span>
      </button>
    </div>
  </Modal>
</template>
